import { useState } from "react";
import { startAuthentication, type AuthenticatorTransportFuture } from "@simplewebauthn/browser";
import { ApiError, type ApiClient, type PasskeyLoginOptions } from "@ferrivia/api-contract";
import type { Tracker } from "@ferrivia/analytics/browser";
import { useTranslation } from "@ferrivia/i18n";
import { useApi, usePasskeyDevice } from "./AuthProvider";
import type { DeviceCredential, PasskeyDeviceStore } from "./passkeyDevice";
import type { SessionCredentials } from "./useSession";

export interface UseSignInOptions {
  onSignedIn: (credentials: SessionCredentials) => void;
  initialErrorCode?: string | null;
  /** Merged over the built-in Finnish copy. */
  errorMessages?: Record<string, string>;
  fallbackErrorMessage?: string;
  /** Defaults to `${window.location.origin}/auth/callback`. */
  returnTo?: string;
  /** Optional — keeps this package free of transport concerns. */
  track?: Tracker;
}

export interface UseSignInResult {
  pending: boolean;
  error: string | null;
  signIn: () => Promise<void>;
}

// The WebAuthn spec conflates "user cancelled" and "no matching credential"
// under NotAllowedError on most platforms, so NotFoundError is the closest
// available signal for a device with no credential at all.
function classifyPasskeyAbandon(err: unknown): "unsupported" | "no_credential" | "cancelled" | "failed" {
  const name = err instanceof Error ? err.name : undefined;
  if (name === "NotAllowedError") return "cancelled";
  if (name === "NotSupportedError" || name === "InvalidStateError") return "unsupported";
  if (name === "NotFoundError") return "no_credential";
  return "failed";
}

/**
 * Name the passkey this browser already uses, so the platform has nothing left
 * to ask. Without an allowlist the ceremony is usernameless and discoverable,
 * and — in @simplewebauthn/server's own words — "if undefined the client will
 * ask the user which credential they want to use". On iOS that question is a
 * full confirmation sheet standing between the tap and Face ID.
 *
 * A client-side filter, not a server-side control, and the server is right not
 * to care: verifyLogin resolves the credential from the assertion, checks the
 * single-use challenge and verifies the signature against the stored public
 * key. Narrowing the browser's own picker is invisible to all of that.
 */
function withAllowedCredential(
  optionsJSON: PasskeyLoginOptions,
  known: DeviceCredential | null,
): PasskeyLoginOptions {
  if (!known) {
    // Nothing remembered — the discoverable ceremony, and the picker with it.
    return optionsJSON;
  }
  return {
    ...optionsJSON,
    allowCredentials: [
      {
        id: known.id,
        type: "public-key",
        // Transports as the authenticator reported them at registration, and
        // absent when a login wrote the record (an assertion carries none).
        // Narrowing to ["internal"] would be a sharper hint but would strand a
        // desktop clinic user whose passkey lives on their phone over hybrid.
        ...(known.transports ? { transports: known.transports as AuthenticatorTransportFuture[] } : {}),
      },
    ],
  };
}

export interface TryPasskeySignInOptions {
  api: ApiClient;
  passkeyDevice: PasskeyDeviceStore;
  track?: Tracker;
}

/**
 * One passkey sign-in attempt. Resolves to the session on success and to null
 * on every kind of failure (unsupported browser, no credential, a cancelled
 * picker), so the caller can fall through to its own next door — bank ID in
 * Finland, an emailed code in the US.
 */
export async function tryPasskeySignIn({
  api,
  passkeyDevice,
  track,
}: TryPasskeySignInOptions): Promise<SessionCredentials | null> {
  if (typeof PublicKeyCredential === "undefined") {
    track?.track("signin.passkey_abandoned", { outcome: "unsupported", allowlisted: false });
    return null;
  }

  const known = passkeyDevice.lastCredential();
  const allowlisted = known !== null;
  track?.track("signin.passkey_attempted", { allowlisted });
  try {
    const optionsJSON = await api.passkeys.loginOptions();
    const response = await startAuthentication({
      optionsJSON: withAllowedCredential(optionsJSON, known),
    });
    const session = await api.passkeys.verifyLogin(response);
    // Signing in with a passkey here proves this browser holds one, which
    // is what AuthGate reads to decide whether to offer enrolment. Marking
    // it on login (not only on enrolment) also covers devices set up
    // before the mark existed.
    passkeyDevice.markEnrolled(session.user.id);
    // Which passkey, so the next sign-in can name it instead of asking.
    // The id is all an assertion carries; rememberCredential keeps any
    // transports a registration already recorded for the same id.
    passkeyDevice.rememberCredential({ id: response.id });
    return { token: session.token, expiresAt: session.expiresAt };
  } catch (err) {
    // A missing passkey, a cancelled native picker and an unsupported
    // browser all land here indistinguishably — silently fall through
    // rather than surface any of them as an error.
    track?.track("signin.passkey_abandoned", { outcome: classifyPasskeyAbandon(err), allowlisted });
    if (allowlisted) {
      // The named credential did not work. Forget it, so the next tap is
      // the discoverable ceremony again rather than the same failure.
      //
      // Not optional: without this a stale id is permanent. Passkey login
      // fails, eID login succeeds, AuthGate sees the device already marked
      // enrolled and skips re-enrolment, so nothing ever rewrites the
      // record — bank ID forever. That is the trap d2db065 fixed for the
      // device mark itself.
      //
      // A deliberate cancel is caught by this too (WebAuthn conflates the
      // two under NotAllowedError), costing one picker on the next tap.
      passkeyDevice.forgetCredential();
    }
    return null;
  }
}

export function useSignIn(options: UseSignInOptions): UseSignInResult {
  const { onSignedIn, initialErrorCode = null, errorMessages, fallbackErrorMessage, returnTo, track } = options;
  const api = useApi();
  const passkeyDevice = usePasskeyDevice();
  const { t } = useTranslation("auth");

  const defaultErrorMessages: Record<string, string> = {
    invalid_state: t("signin.error.invalid_state", "Kirjautuminen epäonnistui. Yritä uudelleen."),
    eid_failed: t("signin.error.eid_failed", "Pankkitunnistautuminen epäonnistui. Yritä uudelleen."),
    handoff_expired: t("signin.error.handoff_expired", "Istunto vanhentui. Yritä kirjautua uudelleen."),
    eid_not_permitted: t("signin.error.eid_not_permitted", "Tällä tilillä ei ole oikeutta kirjautua sisään."),
  };
  const messages = { ...defaultErrorMessages, ...errorMessages };
  function messageFor(code: string): string {
    return messages[code] ?? fallbackErrorMessage ?? t("signin.error.fallback", "Jokin meni pieleen. Yritä uudelleen.");
  }

  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(initialErrorCode ? messageFor(initialErrorCode) : null);

  async function signIn() {
    setPending(true);
    setError(null);
    track?.track("signin.started", {});

    const credentials = await tryPasskeySignIn({ api, passkeyDevice, track });
    if (credentials) {
      onSignedIn(credentials);
      return;
    }

    try {
      const eidReturnTo = returnTo ?? `${window.location.origin}/auth/callback`;
      const { authorizationUrl } = await api.startEid({ returnTo: eidReturnTo });
      track?.track("signin.eid_redirected", {});
      window.location.href = authorizationUrl;
    } catch (err) {
      setPending(false);
      setError(err instanceof ApiError ? messageFor(err.code) : messageFor(""));
    }
  }

  return { pending, error, signIn };
}
